import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CompareProvider } from "@/context/CompareContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import AdminRoute from "@/components/AdminRoute";
import ScrollToTop from "@/components/ScrollToTop";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import IdleTimeout from "@/components/IdleTimeout";

import Home from "@/pages/Home";
const Deals = lazy(() => import("@/pages/Deals"));
const CarRental = lazy(() => import("@/pages/CarRental"));
const Hotels = lazy(() => import("@/pages/Hotels"));
const Flights = lazy(() => import("@/pages/Flights"));
const Birthstones = lazy(() => import("@/pages/Birthstones"));
const GiftFinder = lazy(() => import("@/pages/GiftFinder"));
const FlightsLive = lazy(() => import("@/pages/FlightsLive"));
const TripPlanner = lazy(() => import("@/pages/TripPlanner"));
const Trains = lazy(() => import("@/pages/Trains"));
const Buses = lazy(() => import("@/pages/Buses"));
const Perks = lazy(() => import("@/pages/Perks"));
const ManagePerks = lazy(() => import("@/pages/admin/ManagePerks"));
const LocationDeals = lazy(() => import("@/pages/LocationDeals"));
const CategoryLanding = lazy(() => import("@/pages/CategoryLanding"));
const BrandsIndex = lazy(() => import("@/pages/BrandsIndex"));
const StoresIndex = lazy(() => import("@/pages/StoresIndex"));
const StorePage = lazy(() => import("@/pages/StorePage"));
const BrandHub = lazy(() => import("@/pages/BrandHub"));
const Coupons = lazy(() => import("@/pages/Coupons"));
const Travel = lazy(() => import("@/pages/Travel"));
const TravelQuiz = lazy(() => import("@/pages/TravelQuiz"));
const TravelCity = lazy(() => import("@/pages/TravelCity"));
const Health = lazy(() => import("@/pages/Health"));
const Compare = lazy(() => import("@/pages/Compare"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const ReviewsSlug = lazy(() => import("@/pages/ReviewsSlug"));
const Guides = lazy(() => import("@/pages/Guides"));
const GuideDetail = lazy(() => import("@/pages/GuideDetail"));
const DealDetail = lazy(() => import("@/pages/DealDetail"));
const CouponDetail = lazy(() => import("@/pages/CouponDetail"));
const TravelDetail = lazy(() => import("@/pages/TravelDetail"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const Saved = lazy(() => import("@/pages/Saved"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AffiliateDisclosure = lazy(() => import("@/pages/AffiliateDisclosure"));
const Login = lazy(() => import("@/pages/Login"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Account = lazy(() => import("@/pages/Account"));

const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const ManageDeals = lazy(() => import("@/pages/admin/ManageDeals"));
const ManageCoupons = lazy(() => import("@/pages/admin/ManageCoupons"));
const ManageReviews = lazy(() => import("@/pages/admin/ManageReviews"));
const ManageArticles = lazy(() => import("@/pages/admin/ManageArticles"));
const ManageTravel = lazy(() => import("@/pages/admin/ManageTravel"));
const ManageStores = lazy(() => import("@/pages/admin/ManageStores"));
const ManageSources = lazy(() => import("@/pages/admin/ManageSources"));
const ManageLinks = lazy(() => import("@/pages/admin/ManageLinks"));
const ManageImages = lazy(() => import("@/pages/admin/ManageImages"));
const Analytics = lazy(() => import("@/pages/admin/Analytics"));
const SocialToday = lazy(() => import("@/pages/admin/SocialToday"));
const BrandLogos = lazy(() => import("@/pages/admin/BrandLogos"));
const OfferAlerts = lazy(() => import("@/pages/admin/OfferAlerts"));
const FullReviewsProgress = lazy(() => import("@/pages/admin/FullReviewsProgress"));
const Inbox = lazy(() => import("@/pages/admin/Inbox"));
const ImportReviews = lazy(() => import("@/pages/admin/ImportReviews"));
const AffiliateLinks = lazy(() => import("@/pages/admin/AffiliateLinks"));
const TakeadsMigrate = lazy(() => import("@/pages/admin/TakeadsMigrate"));
const TakeadsEarnings = lazy(() => import("@/pages/admin/TakeadsEarnings"));

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <IdleTimeout />
          <FavoritesProvider>
          <CompareProvider>
          <Suspense fallback={<div className="min-h-screen" data-testid="page-loading" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/category/:slug" element={<CategoryLanding />} />
            <Route path="/brands" element={<BrandsIndex />} />
            <Route path="/stores" element={<StoresIndex />} />
            <Route path="/stores/:slug" element={<StorePage />} />
            <Route path="/brands/:slug" element={<BrandHub />} />
            <Route path="/coupons" element={<Coupons />} />
            <Route path="/deal/:id" element={<DealDetail />} />
            <Route path="/coupon/:id" element={<CouponDetail />} />
            <Route path="/trip/:id" element={<TravelDetail />} />
            <Route path="/travel" element={<Travel />} />
            <Route path="/car-rental" element={<CarRental />} />
            <Route path="/hotels" element={<Hotels />} />
            <Route path="/flights" element={<Flights />} />
            <Route path="/birthstones" element={<Birthstones />} />
            <Route path="/gift-finder" element={<GiftFinder />} />
            <Route path="/flights-live" element={<FlightsLive />} />
            <Route path="/trip-planner" element={<TripPlanner />} />
            <Route path="/trains" element={<Trains />} />
            <Route path="/buses" element={<Buses />} />
            <Route path="/perks" element={<Perks />} />
            <Route path="/travel/quiz" element={<TravelQuiz />} />
            <Route path="/travel/:slug" element={<TravelCity />} />
            <Route path="/health" element={<Health />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/reviews/:slug" element={<ReviewsSlug />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/:slug" element={<GuideDetail />} />
            <Route path="/author/:slug" element={<AuthorPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/saved" element={<Saved />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<Account />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="social" element={<SocialToday />} />
              <Route path="logos" element={<BrandLogos />} />
              <Route path="offer-alerts" element={<OfferAlerts />} />
              <Route path="perks" element={<ManagePerks />} />
              <Route path="full-reviews" element={<FullReviewsProgress />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="deals" element={<ManageDeals />} />
              <Route path="coupons" element={<ManageCoupons />} />
              <Route path="reviews" element={<ManageReviews />} />
              <Route path="import-reviews" element={<ImportReviews />} />
              <Route path="affiliate-links" element={<AffiliateLinks />} />
              <Route path="takeads" element={<TakeadsMigrate />} />
              <Route path="earnings" element={<TakeadsEarnings />} />
              <Route path="guides" element={<ManageArticles />} />
              <Route path="travel" element={<ManageTravel />} />
              <Route path="stores" element={<ManageStores />} />
              <Route path="sources" element={<ManageSources />} />
              <Route path="links" element={<ManageLinks />} />
              <Route path="images" element={<ManageImages />} />
            </Route>
            <Route path=":seoslug" element={<LocationDeals />} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
          </CompareProvider>
          </FavoritesProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
